import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { EVENT_KEYS } from 'constants-events';
import {
  ContentWorkflowEventKey,
  WorkflowTypeEnum,
  WorkflowTargetEnum,
  WorkflowWhenContextEnum,
} from 'constants-workflows';
import { GuestInviteFeatureProperties } from 'lane-shared/types/features/GuestInviteFeatureProperties';

import createWorkflowTargetOptionValue from 'components/workflows/helpers/createWorkflowTargetOptionValue';

import { targetLabels, targetLabelsV2 } from './workflowLabels';
import { useWorkOrderSpecificTeamWorkflowTargetEnabled } from 'lane-shared/hooks/useWorkOrderSpecificTeamWorkflowTargetEnabled';

type OptionType = {
  value: {
    target: WorkflowTargetEnum;
    [key: string]: any;
  };
  label: string;
};

type Props = {
  whenContext?: WorkflowWhenContextEnum;
  guestInviteFeature?: GuestInviteFeatureProperties;
  event?: ContentWorkflowEventKey;
  workflowType?: WorkflowTypeEnum;
  selectUserTypeList?: { name: string; _id: string }[];
  isNewWorkflowsUIEnabled?: boolean;
};

export default function useWorkflowTargetOptions({
  whenContext,
  guestInviteFeature,
  event,
  workflowType,
  selectUserTypeList,
  isNewWorkflowsUIEnabled,
}: Props) {
  const { t } = useTranslation();

  const isWorkOrderSpecificTeamWorkflowTargetEnabled =
    useWorkOrderSpecificTeamWorkflowTargetEnabled();

  // since we use both dataFieldId and target for selected option identification in SelectUser targets
  // and we had to store option value as an object there therefore it was decided to make all targets work
  // this way to ensure consistency and so we may support similar cases for other target in the future
  return useMemo(() => {
    let options: OptionType[] = [];

    if (event && event === EVENT_KEYS.EVENT_CONTENT_LIVE) {
      options.push(
        {
          value: createWorkflowTargetOptionValue(WorkflowTargetEnum.GroupRole),
          label: isNewWorkflowsUIEnabled
            ? t(targetLabelsV2[WorkflowTargetEnum.GroupRole])
            : t(targetLabels[WorkflowTargetEnum.GroupRole]),
        },
        {
          value: createWorkflowTargetOptionValue(WorkflowTargetEnum.User),
          label: t(targetLabels[WorkflowTargetEnum.User]),
        }
      );
    } else {
      options.push(
        {
          value: createWorkflowTargetOptionValue(WorkflowTargetEnum.GroupRole),
          label: isNewWorkflowsUIEnabled
            ? t(targetLabelsV2[WorkflowTargetEnum.GroupRole])
            : t(targetLabels[WorkflowTargetEnum.GroupRole]),
        },
        {
          value: createWorkflowTargetOptionValue(WorkflowTargetEnum.User),
          label: isNewWorkflowsUIEnabled
            ? t(targetLabelsV2[WorkflowTargetEnum.User])
            : t(targetLabels[WorkflowTargetEnum.User]),
        },
        {
          value: createWorkflowTargetOptionValue(
            WorkflowTargetEnum.InteractionCreator
          ),
          label: isNewWorkflowsUIEnabled
            ? t(targetLabelsV2[WorkflowTargetEnum.InteractionCreator])
            : t(targetLabels[WorkflowTargetEnum.InteractionCreator]),
        }
      );

      if (guestInviteFeature) {
        options.push({
          value: createWorkflowTargetOptionValue(
            WorkflowTargetEnum.GuestInviteFeature
          ),
          label: isNewWorkflowsUIEnabled
            ? t(targetLabelsV2[WorkflowTargetEnum.GuestInviteFeature])
            : t(targetLabels[WorkflowTargetEnum.GuestInviteFeature]),
        });
      }

      if (selectUserTypeList?.length) {
        selectUserTypeList.map(item => {
          options.push({
            value: createWorkflowTargetOptionValue(
              WorkflowTargetEnum.SelectUser,
              {
                dataField: {
                  _id: item._id,
                },
              }
            ),
            label: item.name,
          });
        });
      }
    }

    const shouldSkipCreator =
      workflowType === WorkflowTypeEnum.Task ||
      workflowType === WorkflowTypeEnum.ServiceRequestEscalation ||
      workflowType === WorkflowTypeEnum.ServiceRequestReminder;

    if (
      workflowType === WorkflowTypeEnum.ServiceRequest ||
      workflowType === WorkflowTypeEnum.Task ||
      workflowType === WorkflowTypeEnum.ServiceRequestEscalation ||
      workflowType === WorkflowTypeEnum.ServiceRequestReminder
    ) {
      options = [
        {
          value: { target: WorkflowTargetEnum.WorkOrderAdmin },
          label: targetLabelsV2[WorkflowTargetEnum.WorkOrderAdmin],
        },
        {
          value: { target: WorkflowTargetEnum.WorkOrderTeam },
          label: targetLabelsV2[WorkflowTargetEnum.WorkOrderTeam],
        },
        ...(shouldSkipCreator
          ? []
          : [
              {
                value: { target: WorkflowTargetEnum.WorkOrderCreator },
                label: targetLabelsV2[WorkflowTargetEnum.WorkOrderCreator],
              },
            ]),
        ...(isWorkOrderSpecificTeamWorkflowTargetEnabled
          ? [
              {
                value: { target: WorkflowTargetEnum.GroupRole },
                label: targetLabelsV2[WorkflowTargetEnum.GroupRole],
              },
            ]
          : []),
      ];
    }

    return options;
  }, [whenContext, event]);
}
